import React, { useState, useRef, useEffect } from 'react';
import './WaveformAudioPlayer.css';
import { getSafeMediaUrl } from './utils';

// This component generates the visual waveform
const Waveform = ({ progress, onSeek }) => {
    // Generate an array of 50 random heights for the bars
    const bars = React.useMemo(() => Array(50).fill(0).map(() => Math.random() * 0.7 + 0.3), []);

    // Calculate how many bars should be "active" based on progress
    const activeBarsCount = Math.floor(bars.length * progress);

    const handleWaveformClick = (e) => {
        const waveformRect = e.currentTarget.getBoundingClientRect();
        const clickPositionX = e.clientX - waveformRect.left;
        const newProgress = clickPositionX / waveformRect.width;
        onSeek(newProgress);
    };

    return (
        <div className="waveform-container" onClick={handleWaveformClick}>
            {bars.map((height, index) => (
                <div
                    key={index}
                    className={`waveform-bar ${index < activeBarsCount ? 'bar-active' : 'bar-inactive'}`}
                    style={{ height: `${height * 100}%` }}
                />
            ))}
        </div>
    );
};

// The main Player component
const WaveformAudioPlayer = ({ title, audioUrl }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);

    const audioRef = useRef(null);
    const safeAudioUrl = getSafeMediaUrl(audioUrl);

    // Toggle play/pause state
    const togglePlayPause = () => {
        setIsPlaying(!isPlaying);
    };

    // Effect to control audio playback
    useEffect(() => {
        if (isPlaying) {
            audioRef.current.play().catch(error => console.error("Audio play failed:", error));
        } else {
            audioRef.current.pause();
        }
    }, [isPlaying]);

    // Update progress bar as audio plays
    const handleTimeUpdate = () => {
        const currentProgress = (audioRef.current.currentTime / audioRef.current.duration) || 0;
        setProgress(currentProgress);
    };

    // Reset player when audio ends
    const handleAudioEnd = () => {
        setIsPlaying(false);
        setProgress(0);
        audioRef.current.currentTime = 0;
    };

    // Handle seeking when user clicks on the waveform
    const handleSeek = (newProgress) => {
        if (audioRef.current.duration) {
            audioRef.current.currentTime = audioRef.current.duration * newProgress;
            setProgress(newProgress);
        }
    };

    return (
        <div className="audio-player-container">
            <h2 className="audio-title">{title}</h2>
            <div className="player-controls">
                <button className="play-pause-btn" onClick={togglePlayPause}>
                    {isPlaying ? (
                        // Pause Icon (SVG)
                        <svg width="14" height="16" viewBox="0 0 14 16" fill="currentColor">
                            <rect x="0" y="0" width="4" height="16" rx="1"/>
                            <rect x="10" y="0" width="4" height="16" rx="1"/>
                        </svg>
                    ) : (
                        // Play Icon (SVG)
                        <svg width="14" height="16" viewBox="0 0 14 16" fill="currentColor">
                            <path d="M13.5 8L0.5 15.7942V0.205771L13.5 8Z"/>
                        </svg>
                    )}
                </button>
                <Waveform progress={progress} onSeek={handleSeek} />
            </div>
            <audio
                ref={audioRef}
                src={safeAudioUrl}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleAudioEnd}
                // Preload metadata to get duration ASAP
                preload="metadata"
            />
        </div>
    );
};

export default WaveformAudioPlayer;
